import React, { useEffect, useState } from 'react'
import * as Video from 'twilio-video'
import { Room } from 'twilio-video'

import ErrorBoundary from '../../containers/error_boundary'
import { RoomDataType } from '../../contexts/call_manager_context'
import { CE, CS } from './call_manager/connection_status_reducer'

export const CONNECTED = 'connected'
export const RECONNECTING = 'reconnecting'
export const DISCONNECTED = 'disconnected'

export const useRoomManager = (
  connectionStatus: CS,
  prevConnectionStatus: CS,
  dispatchConnectionAction: any,
): {
  room: Room | null
  setRoom: React.Dispatch<React.SetStateAction<Room | null>>
  roomData: RoomDataType
} => {
  const [room, setRoom] = useState<Room | null>(null)
  const [roomData, setRoomData] = useState<RoomDataType>({})

  // Only set the new connection state in these event handlers.
  // Perform any side-effects separately, in reaction to the connection state changing.
  useEffect(() => {
    if (room) {
      const handleTwilioError = (error: Video.TwilioError) => {
        ErrorBoundary.logErrorToMyService(error)
      }

      const reconnected = () => {
        dispatchConnectionAction(CE.ROOM_CONNECTED)
      }

      const reconnecting = (error: Video.TwilioError) => {
        dispatchConnectionAction(CE.ROOM_RECONNECTING)
        handleTwilioError(error)
      }

      const disconnected = (room: Video.Room, error: Video.TwilioError) => {
        dispatchConnectionAction(CE.ROOM_DISCONNECTED)
        handleTwilioError(error)
      }

      room.on('reconnecting', reconnecting)
      room.on('reconnected', reconnected)
      room.on('disconnected', disconnected)

      return () => {
        room.off('reconnecting', reconnecting)
        room.off('reconnected', reconnected)
        room.off('disconnected', disconnected)
      }
    }
  }, [dispatchConnectionAction, room])

  useEffect(() => {
    const roomClosed =
      (prevConnectionStatus === CS.ROOM_CONNECTED ||
        prevConnectionStatus === CS.ROOM_RECONNECTING) &&
      connectionStatus === CS.CALL_CONNECTED
    const disconnected =
      prevConnectionStatus !== CS.DISCONNECTED &&
      connectionStatus === CS.DISCONNECTED
    if (roomClosed || disconnected) {
      if (room) {
        setRoomData((prevState) => {
          return {
            ...prevState,
            initialAudioDeviceId: undefined,
            initialVideoDeviceId: undefined,
            lastClosedRoomSid: room.sid,
          }
        })
        setRoom(null)
        room.localParticipant.removeAllListeners()
      }
    }
  }, [connectionStatus, prevConnectionStatus, room])

  return { roomData, room, setRoom }
}
